import { useEffect, useRef, useState, type ReactNode } from "react";
import Hls from "hls.js";
import { Maximize2, Minimize2, RefreshCw, VideoOff } from "lucide-react";

const MAX_RETRIES = 5;
const RETRY_BASE_DELAY_MS = 1500;

export function LiveVideoPlayer({
  src,
  className = "",
  overlay,
}: {
  src: string;
  className?: string;
  overlay?: ReactNode;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const retryCountRef = useRef(0);
  const lastSrcRef = useRef(src);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [error, setError] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  // Cambiar esto fuerza al efecto de abajo a reconectar desde cero (reintento manual o automático).
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Solo reiniciamos el contador de reintentos cuando cambia la cámara/URL de verdad, no
    // cuando el propio reintento automático dispara este efecto vía "attempt".
    if (lastSrcRef.current !== src) {
      lastSrcRef.current = src;
      retryCountRef.current = 0;
    }

    setError(false);

    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      // Safari sabe reproducir HLS de forma nativa, sin hls.js.
      video.src = src;
      return;
    }

    if (!Hls.isSupported()) {
      setError(true);
      return;
    }

    const hls = new Hls({ lowLatencyMode: true });
    hlsRef.current = hls;

    hls.on(Hls.Events.ERROR, (_event, data) => {
      if (!data.fatal) return;

      // Fallos transitorios (cámara reconectando, arranque en frío del relay, red inestable):
      // reintentamos solos con backoff antes de rendirnos, en vez de dejar la señal "muerta"
      // hasta que alguien recargue la página a mano.
      if (retryCountRef.current < MAX_RETRIES) {
        const attemptNumber = retryCountRef.current + 1;
        retryCountRef.current = attemptNumber;
        const delay = RETRY_BASE_DELAY_MS * attemptNumber;
        retryTimeoutRef.current = setTimeout(() => setAttempt((a) => a + 1), delay);
      } else {
        setError(true);
      }
    });

    hls.loadSource(src);
    hls.attachMedia(video);

    return () => {
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
      hls.destroy();
      hlsRef.current = null;
    };
  }, [src, attempt]);

  useEffect(() => {
    function onFullscreenChange() {
      setIsFullscreen(document.fullscreenElement === containerRef.current);
    }
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, []);

  function toggleFullscreen() {
    // Agrandamos el contenedor (vídeo + marcador), no el <video> a solas, para que el
    // overlay del marcador siga visible también en pantalla completa.
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      containerRef.current?.requestFullscreen();
    }
  }

  function retryNow() {
    retryCountRef.current = 0;
    setError(false);
    setAttempt((a) => a + 1);
  }

  return (
    <div ref={containerRef} className={`group relative bg-black ${isFullscreen ? "flex items-center" : ""} ${className}`}>
      {error ? (
        <div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-ink-900 text-ink-300">
          <VideoOff className="h-6 w-6" />
          <span className="text-xs">Sin señal de la cámara</span>
          <button
            type="button"
            onClick={retryNow}
            className="mt-1 flex items-center gap-1.5 rounded-lg bg-white/10 px-2.5 py-1 text-xs text-white transition-colors hover:bg-white/20"
          >
            <RefreshCw className="h-3 w-3" />
            Reintentar
          </button>
        </div>
      ) : (
        <video ref={videoRef} autoPlay muted playsInline controls controlsList="nofullscreen" className="h-full w-full object-contain" />
      )}
      {!error && overlay}
      <button
        type="button"
        onClick={toggleFullscreen}
        title={isFullscreen ? "Salir de pantalla completa" : "Agrandar"}
        className="absolute right-2 bottom-2 rounded-lg bg-ink-950/70 p-1.5 text-white opacity-0 transition-opacity group-hover:opacity-100"
      >
        {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
      </button>
    </div>
  );
}
